<template>
  <div class="rte">
    <div v-if="editor" class="rte-toolbar">
      <div class="rte-group">
        <button v-for="btn in formatBtns" :key="btn.id" class="rte-btn" :class="{ active: btn.check() }" @click="btn.action()" :title="btn.title">
          <span v-if="btn.text" v-html="btn.text"></span>
          <feather-icon v-else :icon="btn.icon" size="14" />
        </button>
      </div>
      <div class="rte-sep"></div>
      <div class="rte-group">
        <button class="rte-btn" :class="{ active: editor.isActive('heading', { level: 2 }) }" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" title="Título H2">H2</button>
        <button class="rte-btn" :class="{ active: editor.isActive('heading', { level: 3 }) }" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()" title="Título H3">H3</button>
      </div>
      <div class="rte-sep"></div>
      <div class="rte-group">
        <button class="rte-btn" :class="{ active: editor.isActive('bulletList') }" @click="editor.chain().focus().toggleBulletList().run()" title="Lista">
          <feather-icon icon="ListIcon" size="14" />
        </button>
        <button class="rte-btn" :class="{ active: editor.isActive('orderedList') }" @click="editor.chain().focus().toggleOrderedList().run()" title="Lista Numerada">
          <span style="font-size:12px;font-weight:600">1.</span>
        </button>
        <button class="rte-btn" :class="{ active: editor.isActive('blockquote') }" @click="editor.chain().focus().toggleBlockquote().run()" title="Citação">
          <feather-icon icon="MessageSquareIcon" size="14" />
        </button>
      </div>
      <div class="rte-sep"></div>
      <div class="rte-group">
        <button v-for="al in aligns" :key="al.val" class="rte-btn" :class="{ active: editor.isActive({ textAlign: al.val }) }" @click="editor.chain().focus().setTextAlign(al.val).run()" :title="al.title">
          <feather-icon :icon="al.icon" size="14" />
        </button>
      </div>
      <div class="rte-sep"></div>
      <div class="rte-group">
        <button class="rte-btn" @click="insertLink" title="Link"><feather-icon icon="LinkIcon" size="14" /></button>
        <button class="rte-btn" @click="insertImage" title="Imagem"><feather-icon icon="ImageIcon" size="14" /></button>
      </div>
      <div class="rte-spacer"></div>
      <div class="rte-group">
        <button class="rte-btn" @click="editor.chain().focus().undo().run()" title="Desfazer"><feather-icon icon="CornerUpLeftIcon" size="14" /></button>
        <button class="rte-btn" @click="editor.chain().focus().redo().run()" title="Refazer"><feather-icon icon="CornerUpRightIcon" size="14" /></button>
      </div>
    </div>
    <editor-content :editor="editor" class="rte-content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { BButton, BButtonGroup } from 'bootstrap-vue'

export default {
  name: 'RichTextEditor',
  components: { EditorContent, BButton, BButtonGroup },
  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: 'Comece a escrever seu artigo...' },
  },
  data() {
    return {
      editor: null,
      aligns: [
        { val: 'left', icon: 'AlignLeftIcon', title: 'Esquerda' },
        { val: 'center', icon: 'AlignCenterIcon', title: 'Centro' },
        { val: 'right', icon: 'AlignRightIcon', title: 'Direita' },
        { val: 'justify', icon: 'AlignJustifyIcon', title: 'Justificar' },
      ],
    }
  },
  computed: {
    formatBtns() {
      if (!this.editor) return []
      return [
        { id: 'bold', text: '<strong>B</strong>', check: () => this.editor.isActive('bold'), action: () => this.editor.chain().focus().toggleBold().run(), title: 'Negrito' },
        { id: 'italic', text: '<em>I</em>', check: () => this.editor.isActive('italic'), action: () => this.editor.chain().focus().toggleItalic().run(), title: 'Itálico' },
        { id: 'underline', text: '<u>U</u>', check: () => this.editor.isActive('underline'), action: () => this.editor.chain().focus().toggleUnderline().run(), title: 'Sublinhado' },
      ]
    },
  },
  watch: {
    value(val) {
      if (this.editor && val !== this.editor.getHTML()) {
        this.editor.commands.setContent(val, false)
      }
    },
  },
  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Image.configure({ inline: false, allowBase64: true }),
        Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary' } }),
        Placeholder.configure({ placeholder: this.placeholder }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Underline,
      ],
      onUpdate: ({ editor }) => {
        this.$emit('input', editor.getHTML())
      },
    })
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy()
  },
  methods: {
    insertLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('URL do link:', previousUrl)
      if (url === null) return
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    },
    insertImage() {
      const url = window.prompt('URL da imagem:')
      if (url) {
        this.editor.chain().focus().setImage({ src: url }).run()
      }
    },
  },
}
</script>

<style>
.rte{border:1.5px solid #e2e8f0;border-radius:12px;overflow:hidden;background:#fff;transition:border-color .2s}
.rte:focus-within{border-color:#7c3aed;box-shadow:0 0 0 3px rgba(124,58,237,.08)}

.rte-toolbar{display:flex;align-items:center;gap:2px;padding:8px 12px;background:#fafbfc;border-bottom:1px solid #f1f5f9;flex-wrap:wrap}
.rte-group{display:flex;gap:2px}
.rte-sep{width:1px;height:20px;background:#e2e8f0;margin:0 4px}
.rte-spacer{flex:1}

.rte-btn{width:30px;height:30px;border-radius:6px;border:none;background:transparent;color:#64748b;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;font-size:13px;font-weight:600;font-family:inherit}
.rte-btn:hover{background:#f1f5f9;color:#475569}
.rte-btn.active{background:#7c3aed;color:#fff}

.rte-content .ProseMirror{padding:20px 24px;min-height:400px;outline:none;font-size:.95rem;line-height:1.75;color:#1a1a2e}
.rte-content .ProseMirror h2{font-size:1.5rem;font-weight:700;margin:1.8rem 0 .8rem;color:#1a1a2e}
.rte-content .ProseMirror h3{font-size:1.2rem;font-weight:700;margin:1.4rem 0 .6rem;color:#1a1a2e}
.rte-content .ProseMirror p{margin:.5rem 0}
.rte-content .ProseMirror blockquote{border-left:3px solid #7c3aed;padding-left:16px;margin:1.2rem 0;color:#64748b;font-style:italic}
.rte-content .ProseMirror ul,.rte-content .ProseMirror ol{padding-left:1.5rem}
.rte-content .ProseMirror img{max-width:100%;border-radius:10px;margin:16px 0}
.rte-content .ProseMirror a{color:#7c3aed;text-decoration:underline}
.rte-content .ProseMirror p.is-editor-empty:first-child::before{content:attr(data-placeholder);float:left;color:#cbd5e1;pointer-events:none;height:0;font-style:italic}
</style>
